import React, { useEffect, useMemo, useState } from 'react';
import { fetchCourseChapters, fetchChapterContent, linkScheduleContents } from '../../../api/adminApi';
import { buildChapterTree, activeContentNodeId } from '../scheduleBuilder/chapterTree';

async function fetchAllCourseChapters(courseId) {
  const all = [];
  let page = 1;
  let totalPages = 1;
  do {
    const res = await fetchCourseChapters(courseId, page, 100);
    all.push(...(res.chapters || []));
    totalPages = res.totalPages || 1;
    page += 1;
  } while (page <= totalPages);
  return all;
}

/** Content types that satisfy a schedule flag / mode. */
function allowedTypes(flag, mode) {
  if (flag === 'missing_exam') return new Set(['Exam']);
  // A Revision Live session wants the same thing a Live one does: the
  // exam-discussion conference, once it exists upstream.
  if (flag === 'missing_live' || flag === 'revision_live_unlinked') {
    return new Set(['VideoConference']);
  }
  if (mode === 'Exam' || mode === 'Unit Test') return new Set(['Exam']);
  if (mode === 'Live') return new Set(['VideoConference']);
  // An Offline class is taught in person and has nothing to link.
  if (mode === 'Offline') return new Set();
  return null;
}

const LinkContentPanel = ({
  scheduleIds,
  flag,
  mode,
  courses = [],
  onLinked,
}) => {
  const [open, setOpen] = useState(false);
  const [courseId, setCourseId] = useState('');
  const [roots, setRoots] = useState([]);
  const [subjectId, setSubjectId] = useState('');
  const [chapterId, setChapterId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [contents, setContents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (courses.length && !courseId) setCourseId(String(courses[0].id));
  }, [courses, courseId]);

  const subjects = roots;
  const chapters = useMemo(() => {
    const subject = subjects.find((node) => String(node.id) === String(subjectId));
    return subject ? subject.children : [];
  }, [subjects, subjectId]);
  const topics = useMemo(() => {
    const chapter = chapters.find((node) => String(node.id) === String(chapterId));
    return chapter ? chapter.children : [];
  }, [chapters, chapterId]);

  const nodeId = activeContentNodeId({ subjectId, chapterId, topicId });
  const typeFilter = allowedTypes(flag, mode);

  useEffect(() => {
    setRoots([]);
    setSubjectId('');
    setChapterId('');
    setTopicId('');
    setContents([]);
    setSelectedIds([]);
    setDone(false);
    setError(null);
    if (!courseId) return undefined;

    let active = true;
    (async () => {
      try {
        const flat = await fetchAllCourseChapters(courseId);
        if (active) setRoots(buildChapterTree(flat));
      } catch (loadError) {
        if (active) setError('Failed to load chapter tree.');
      }
    })();
    return () => { active = false; };
  }, [courseId]);

  useEffect(() => {
    setContents([]);
    setSelectedIds([]);
    if (nodeId == null) return undefined;

    let active = true;
    (async () => {
      try {
        const res = await fetchChapterContent(nodeId, 1, 100);
        if (active) setContents(res.content || []);
      } catch (loadError) {
        if (active) setError('Failed to load content.');
      }
    })();
    return () => { active = false; };
  }, [nodeId]);

  const visibleContents = useMemo(() => {
    if (!typeFilter) return contents;
    return contents.filter((item) => typeFilter.has(item.content_type));
  }, [contents, typeFilter]);

  const toggle = (contentId) => {
    setSelectedIds((prev) => (
      prev.includes(contentId)
        ? prev.filter((id) => id !== contentId)
        : [...prev, contentId]
    ));
  };

  const submit = async () => {
    if (!selectedIds.length) return;
    setBusy(true);
    setError(null);
    try {
      await linkScheduleContents({
        scheduleIds,
        contentIds: selectedIds,
        chapterId: nodeId ?? undefined,
      });
      setDone(true);
      if (onLinked) onLinked();
    } catch (linkError) {
      setError(linkError?.response?.data?.message || 'Could not link content.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return <div className="cal-link-done">Content linked across all batches.</div>;
  }

  return (
    <div className="cal-link">
      <button
        type="button"
        className="cal-link-toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <i className={`bi bi-${open ? 'chevron-down' : 'chevron-right'}`} aria-hidden="true" />
        Link content manually
        <span className="cal-link-hint">subject → chapter → topic</span>
      </button>

      {open && (
        <div className="cal-link-body">
          {error && <div className="cal-error">{error}</div>}

          {courses.length > 1 && (
            <select
              className="cal-select"
              value={courseId}
              onChange={(event) => setCourseId(event.target.value)}
              aria-label="Course"
            >
              {courses.map((course) => (
                <option key={course.id} value={course.id}>{course.title || `Course ${course.id}`}</option>
              ))}
            </select>
          )}

          <div className="cal-link-tree">
            <select
              className="cal-select"
              value={subjectId}
              onChange={(event) => { setSubjectId(event.target.value); setChapterId(''); setTopicId(''); }}
              aria-label="Subject"
              disabled={!courseId}
            >
              <option value="">Select subject…</option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>{subject.name || `Subject ${subject.id}`}</option>
              ))}
            </select>
            <select
              className="cal-select"
              value={chapterId}
              onChange={(event) => { setChapterId(event.target.value); setTopicId(''); }}
              aria-label="Chapter"
              disabled={!subjectId || chapters.length === 0}
            >
              <option value="">{chapters.length ? 'Select chapter…' : 'No chapters'}</option>
              {chapters.map((chapter) => (
                <option key={chapter.id} value={chapter.id}>{chapter.name || `Chapter ${chapter.id}`}</option>
              ))}
            </select>
            <select
              className="cal-select"
              value={topicId}
              onChange={(event) => setTopicId(event.target.value)}
              aria-label="Topic"
              disabled={!chapterId || topics.length === 0}
            >
              <option value="">{topics.length ? 'Select topic…' : 'No topics (optional)'}</option>
              {topics.map((topic) => (
                <option key={topic.id} value={topic.id}>{topic.name || `Topic ${topic.id}`}</option>
              ))}
            </select>
          </div>

          {nodeId != null && (
            <div className="cal-link-contents">
              {visibleContents.length === 0 && (
                <span className="cal-empty">No matching content in this node.</span>
              )}
              {visibleContents.map((item) => (
                <label key={item.id} className={`sb-contentitem${selectedIds.includes(item.id) ? ' active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={() => toggle(item.id)}
                  />
                  <span className="sb-content-title">{item.title}</span>
                  <span className="sb-content-type">{item.content_type}</span>
                </label>
              ))}
            </div>
          )}

          <div className="cal-link-actions">
            <button
              type="button"
              className="sb-btn sb-btn-sm sb-btn-primary"
              disabled={busy || selectedIds.length === 0}
              onClick={submit}
            >
              {busy ? 'Linking…' : `Link to ${scheduleIds.length} batch${scheduleIds.length === 1 ? '' : 'es'}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkContentPanel;
